import React from 'react';
import { MatchEvent, Language, Game } from '../../types';
import { SoccerBallIcon } from './MatchIcons';
import { Repeat, Clock, PlayCircle } from 'lucide-react';
import { t } from '../../i18n';

interface MatchTimelineTabProps {
    events: MatchEvent[];
    lang: Language;
    status?: Game['status'];
    currentMinute?: number;
    variant?: 'vertical' | 'horizontal';
    homeLogo?: string;
    awayLogo?: string;
    onPlayerClick?: (player: any) => void;
}

export const MatchTimelineTab: React.FC<MatchTimelineTabProps> = ({ 
    events, 
    lang, 
    status, 
    currentMinute,
    variant = 'vertical',
    homeLogo,
    awayLogo,
    onPlayerClick
}) => {
    const hasStarted = status && status !== 'Scheduled';
    const isLive = status === 'Live';
    const isFinished = status === 'Finished';
    const hasEvents = events && events.length > 0;

    if (variant === 'horizontal') {
        if (!hasStarted) {
            return (
                <div className="w-full relative py-8">
                    {/* Fixed Team Logos at the Left */}
                    <div className="absolute left-2 top-0 bottom-0 flex flex-col items-center justify-center z-20 pointer-events-none">
                        <div className="relative h-full flex flex-col items-center justify-center">
                            {/* Home Logo (Top) */}
                            <div className="absolute bottom-1/2 mb-8 w-10 h-10 p-1 bg-white border border-slate-100 rounded-xl shadow-md flex items-center justify-center pointer-events-auto">
                                <img src={homeLogo} alt="" className="w-full h-full object-contain" />
                            </div>

                            {/* Middle Spacer or Icon can go here if needed */}
                            <div className="w-10 h-10 shrink-0"></div>

                            {/* Away Logo (Bottom) */}
                            <div className="absolute top-1/2 mt-8 w-10 h-10 p-1 bg-white border border-slate-100 rounded-xl shadow-md flex items-center justify-center pointer-events-auto">
                                <img src={awayLogo} alt="" className="w-full h-full object-contain" />
                            </div>
                        </div>
                    </div>

                    <div className="w-full overflow-hidden overflow-x-auto no-scrollbar pl-0 pr-4">
                        <div className="relative min-w-[600px] flex items-center justify-start py-12">
                            {/* Horizontal Line */}
                            <div className="absolute left-0 right-0 top-1/2 h-[1px] bg-slate-100 -translate-y-1/2" />
                            
                            {/* Start Node (0') */}
                            <div className="relative flex flex-col items-center z-10 mx-2 shrink-0">
                                <div className="w-10 h-10 rounded-full bg-white border border-slate-100 shadow-sm flex items-center justify-center text-[11px] font-black text-slate-900 ring-4 ring-white">
                                    0'
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            );
        }
        
        if ((isLive || isFinished) && !hasEvents) {
            return (
                <div className="w-full relative py-8 px-6 flex flex-col items-center justify-center min-h-[160px]">
                    <h3 className="text-sm font-black text-slate-400 mb-2">{t(lang, 'awaitingData')}</h3>
                    <p className="text-[10px] font-bold text-slate-300 leading-relaxed text-center">{t(lang, 'awaitingDataDesc')}</p>
                </div>
            );
        }
        
        const sortedEvents = [...events]
            .filter(e => e.minute >= 0)
            .sort((a, b) => a.minute - b.minute);

        return (
            <div className="w-full relative py-8">
                {/* Fixed Team Logos at the Left */}
                <div className="absolute left-2 top-0 bottom-0 flex flex-col items-center justify-center z-20 pointer-events-none">
                    <div className="relative h-full flex flex-col items-center justify-center">
                        {/* Home Logo (Top) */}
                        <div className="absolute bottom-1/2 mb-8 w-10 h-10 p-1 bg-white border border-slate-100 rounded-xl shadow-md flex items-center justify-center pointer-events-auto">
                            <img src={homeLogo} alt="" className="w-full h-full object-contain" />
                        </div>

                        {/* Middle Spacer or Icon can go here if needed */}
                        <div className="w-10 h-10 shrink-0"></div>

                        {/* Away Logo (Bottom) */}
                        <div className="absolute top-1/2 mt-8 w-10 h-10 p-1 bg-white border border-slate-100 rounded-xl shadow-md flex items-center justify-center pointer-events-auto">
                            <img src={awayLogo} alt="" className="w-full h-full object-contain" />
                        </div>
                    </div>
                </div>

                <div className="w-full overflow-hidden overflow-x-auto no-scrollbar pl-0 pr-4">
                    <div className="relative min-w-[600px] flex items-center justify-start py-12">
                        {/* Horizontal Line */}
                        <div className="absolute left-0 right-0 top-1/2 h-[1px] bg-slate-100 -translate-y-1/2" />
                        
                        {/* Start Node (0') */}
                        <div className="relative flex flex-col items-center z-10 mx-2 shrink-0">
                            <div className="w-10 h-10 rounded-full bg-white border border-slate-100 shadow-sm flex items-center justify-center text-[11px] font-black text-slate-900 ring-4 ring-white">
                                0'
                            </div>
                        </div>

                        {/* Current Minute Node (Live) */}
                        {status === 'Live' && currentMinute && (
                            <div className="relative flex flex-col items-center z-10 mx-7 shrink-0">
                                <div className="absolute bottom-full mb-3 flex flex-col items-center">
                                    <span className="text-[8px] font-black text-rose-500 uppercase tracking-widest animate-pulse">Live</span>
                                </div>
                                <div className="w-10 h-10 rounded-full bg-rose-50 border-2 border-rose-500 shadow-sm flex items-center justify-center text-[11px] font-black text-rose-600 ring-4 ring-white animate-pulse">
                                    {currentMinute}'
                                </div>
                            </div>
                        )}

                        {/* Events */}
                        {sortedEvents.map((e, i) => {
                            const isHome = e.team === 'home';
                            return (
                                <div key={i} className="relative flex flex-col items-center z-10 mx-7 shrink-0">
                                    {/* Top Content (Home Events) */}
                                    <div className={`absolute bottom-full mb-3 flex flex-col items-center w-[120px] transition-all ${isHome ? 'opacity-100' : 'opacity-0'}`}>
                                        <button 
                                            onClick={() => isHome && onPlayerClick && onPlayerClick({ 
                                                id: e.player_id, 
                                                name: e.player,
                                                photo: `https://media.api-sports.io/football/players/${e.player_id}.png`
                                            })}
                                            disabled={!isHome || !onPlayerClick}
                                            className="text-[9px] font-black text-slate-900 uppercase tracking-tight text-center line-clamp-1 mb-1 hover:text-rose-500 transition-colors cursor-pointer disabled:cursor-default"
                                        >
                                            {e.player}
                                        </button>
                                        {e.type === 'goal' && <SoccerBallIcon className="w-4 h-4 text-slate-900" />}
                                        {e.type === 'card' && (
                                            <div className={`w-3 h-4 rounded-[2px] ${e.detail?.toLowerCase().includes('red') ? 'bg-rose-600' : 'bg-yellow-400'}`} />
                                        )}
                                        {e.type === 'sub' && <Repeat size={16} className="text-indigo-500" strokeWidth={3} />}
                                    </div>

                                    {/* Minute Node */}
                                    <div className="w-10 h-10 rounded-full bg-white border border-slate-100 shadow-sm flex items-center justify-center text-[11px] font-black text-slate-900 ring-4 ring-white">
                                        {e.minute}'
                                    </div>

                                    {/* Bottom Content (Away Events) */}
                                    <div className={`absolute top-full mt-3 flex flex-col items-center w-[120px] transition-all ${!isHome ? 'opacity-100' : 'opacity-0'}`}>
                                        {e.type === 'goal' && <SoccerBallIcon className="w-4 h-4 text-slate-900" />}
                                        {e.type === 'card' && (
                                            <div className={`w-3 h-4 rounded-[2px] ${e.detail?.toLowerCase().includes('red') ? 'bg-rose-600' : 'bg-yellow-400'}`} />
                                        )}
                                        {e.type === 'sub' && <Repeat size={14} className="text-indigo-500" strokeWidth={3} />}
                                        <button 
                                            onClick={() => !isHome && onPlayerClick && onPlayerClick({ 
                                                id: e.player_id, 
                                                name: e.player,
                                                photo: `https://media.api-sports.io/football/players/${e.player_id}.png`
                                            })}
                                            disabled={isHome || !onPlayerClick}
                                            className="text-[9px] font-black text-slate-900 uppercase tracking-tight text-center line-clamp-1 mt-1 hover:text-rose-500 transition-colors cursor-pointer disabled:cursor-default"
                                        >
                                            {e.player}
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="animate-in fade-in duration-500">
            {status !== 'Scheduled' ? (
                (isLive || isFinished) && !hasEvents ? (
                    <div className="py-32 text-center px-6">
                        <div className="w-16 h-16 bg-slate-50 rounded-2xl flex items-center justify-center mx-auto mb-6">
                            <Clock size={32} className="text-slate-200" />
                        </div>
                        <h3 className="text-lg font-black text-slate-800 mb-2">{t(lang, 'awaitingData')}</h3>
                        <p className="text-xs font-bold text-slate-400 leading-relaxed max-w-[240px] mx-auto">{t(lang, 'awaitingDataDesc')}</p>
                    </div>
                ) : (
                <div className="py-12 md:py-16">
                    <div className="relative max-w-lg mx-auto">
                        {/* Central vertical line */}
                        <div className='absolute left-1/2 top-0 bottom-0 w-[1px] bg-slate-100 -translate-x-1/2' />
                        
                        <div className='space-y-12 relative z-10'>
                            {/* Match Started Node - 0' */}
                            <div className="flex items-center gap-6 md:gap-10">
                                <div className="flex-1 text-right">
                                    <span className="text-[11px] md:text-sm font-black uppercase tracking-[0.1em] text-slate-300 opacity-0">
                                        {t(lang, 'matchStarted')}
                                    </span>
                                </div>
                                <div className='w-12 h-12 md:w-14 md:h-14 rounded-full bg-white border border-slate-100 shadow-[0_4px_20px_rgba(0,0,0,0.03)] flex items-center justify-center text-[13px] md:text-[15px] font-black text-slate-900 tabular-nums shrink-0 ring-4 ring-white'>
                                    0'
                                </div>
                                <div className='flex-1 flex items-center gap-2 opacity-0'>
                                    <PlayCircle size={14} className="text-slate-200" />
                                </div>
                            </div>

                            {/* Chronological events */}
                            {events && [...events].filter(e => e.minute >= 0).sort((a,b) => a.minute - b.minute).map((e, i) => {
                                const isHome = e.team === 'home';
                                return (
                                    <div
                                        key={i}
                                        className={`flex items-center gap-6 md:gap-10 ${
                                            isHome ? 'flex-row' : 'flex-row-reverse'
                                        }`}
                                    >
                                        <div
                                            className={`flex-1 ${
                                                isHome ? 'text-right pr-1' : 'text-left pl-1'
                                            }`}
                                        >
                                            <div className={`inline-flex flex-col ${isHome ? 'items-end' : 'items-start'}`}>
                                                <div
                                                    className={`flex items-center gap-2 md:gap-3 mb-0.5 ${
                                                        isHome ? 'flex-row' : 'flex-row-reverse'
                                                    }`}
                                                >
                                                    <button 
                                                        onClick={() => onPlayerClick && onPlayerClick({ 
                                                            id: e.player_id, 
                                                            name: e.player,
                                                            photo: `https://media.api-sports.io/football/players/${e.player_id}.png`
                                                        })}
                                                        className='text-sm md:text-base font-black uppercase tracking-tight text-slate-900 hover:text-rose-500 transition-colors'
                                                    >
                                                        {e.player}
                                                    </button>
                                                    <div
                                                        className={`w-7 h-7 md:w-8 md:h-8 rounded-xl flex items-center justify-center border border-slate-100 bg-white shadow-sm shrink-0 ${
                                                            e.type === 'goal'
                                                                ? 'text-emerald-500'
                                                                : e.type === 'card'
                                                                    ? 'text-amber-500'
                                                                    : 'text-indigo-500'
                                                        }`}
                                                    >
                                                        {e.type === 'goal' ? (
                                                            <SoccerBallIcon className='w-3.5 h-3.5' />
                                                        ) : e.type === 'card' ? (
                                                            <div
                                                                className={`w-2.5 h-3.5 rounded-[2px] ${
                                                                    e.detail?.toLowerCase().includes('red')
                                                                        ? 'bg-rose-600'
                                                                        : 'bg-yellow-400'
                                                                }`}
                                                            />
                                                        ) : (
                                                            <Repeat size={14} strokeWidth={3} />
                                                        )}
                                                    </div>
                                                </div>
                                                {e.assist && (
                                                    <span className='text-[9px] md:text-[10px] font-black text-slate-300 uppercase tracking-widest'>
                                                        AST: {e.assist}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                        
                                        <div className='w-12 h-12 md:w-14 md:h-14 rounded-full bg-white border border-slate-100 shadow-[0_4px_20px_rgba(0,0,0,0.03)] flex items-center justify-center text-[13px] md:text-[15px] font-black text-slate-900 tabular-nums shrink-0 ring-4 ring-white animate-in zoom-in duration-500'>
                                            {e.minute}'
                                        </div>
                                        
                                        <div className='flex-1' />
                                    </div>
                                );
                            })}

                            {/* Current Minute Node (Live) */}
                            {status === 'Live' && currentMinute && (
                                <div className="flex items-center gap-6 md:gap-10">
                                    <div className="flex-1 text-right">
                                        <span className="text-[10px] md:text-xs font-black text-rose-500 uppercase tracking-widest animate-pulse">
                                            Live
                                        </span>
                                    </div>
                                    <div className='w-12 h-12 md:w-14 md:h-14 rounded-full bg-rose-50 border-2 border-rose-500 shadow-[0_4px_20px_rgba(244,63,94,0.15)] flex items-center justify-center text-[13px] md:text-[15px] font-black text-rose-600 tabular-nums shrink-0 ring-4 ring-white animate-pulse'>
                                        {currentMinute}'
                                    </div>
                                    <div className='flex-1' />
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                )
            ) : (
                <div className="py-32 text-center flex flex-col items-center justify-center gap-6">
                    <div className="w-20 h-20 bg-slate-50 rounded-[40px] border border-slate-100 flex items-center justify-center text-slate-200">
                        <Clock size={40} strokeWidth={1.5} />
                    </div>
                    <p className="text-xs font-black text-slate-300 uppercase tracking-[0.2em]">{t(lang, 'waitingKickoff')}</p>
                </div>
            )}
        </div>
    );
};
